import {
  Banner,
  BannerDescription,
  Box,
  EmptyState,
  EmptyStateDescription,
  EmptyStateGraphic,
  EmptyStateTitle,
  Icon,
  Image,
  InlineStack,
  Link,
  PlainButton,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
} from '@customerio/pluma-components/react';
import { MAX_PREVIEW_ROWS, previewErrorCopy, previewWarningCopy } from '../preview';

type PreviewTableProps = {
  columns: readonly string[];
  rows: readonly (readonly string[])[];
  /** The engine's logo for the empty state. */
  imageSrc: string;
};

/** The first rows a query returned, numbered, or an empty state when it returned none. */
export function PreviewTable({ columns, rows, imageSrc }: PreviewTableProps) {
  if (rows.length === 0) {
    return (
      <EmptyState data-test-preview-table-blank-slate>
        <EmptyStateGraphic>
          <Image src={imageSrc} alt="" size="icon-v2-xl" />
        </EmptyStateGraphic>
        <EmptyStateTitle>No results found</EmptyStateTitle>
        <EmptyStateDescription>
          It looks like your query returned no data. Maybe change it and try again?
        </EmptyStateDescription>
      </EmptyState>
    );
  }
  const shown = rows.slice(0, MAX_PREVIEW_ROWS);
  return (
    <Box>
      <InlineStack alignBlock="start" alignInline="space-between" gap="200" mt="200">
        <Text weight="bold">
          <Icon name="tip" size="sm" color="subtle" mr="050" />
          Make sure that your column names reflect the{' '}
          <Link href="https://docs.customer.io/sql-sync/#mapping-columns-to-attributes" variant="secondary" isExternal>
            correct attribute names
          </Link>{' '}
          and values.
        </Text>
        <Text size="xxs" color="subtle">
          <Icon name="help" size="sm" color="subtle" /> Preview shows the first {MAX_PREVIEW_ROWS} rows returned by your
          query.
        </Text>
      </InlineStack>
      <Box mt="100" style={{ maxHeight: '400px', overflow: 'auto' }}>
        <Table caption="Preview of the results from your query" data-test-preview-table>
          <TableThead>
            <TableTr data-test-preview-table-header>
              <TableTh>
                <Text color="subtle">#</Text>
              </TableTh>
              {columns.map((column, index) => (
                // A join can return the same name twice, so the position is the identity.
                <TableTh key={index}>{column}</TableTh>
              ))}
            </TableTr>
          </TableThead>
          <TableTbody>
            {shown.map((row, index) => (
              <TableTr key={index} data-test-preview-table-row>
                <TableTd>
                  <Text color="subtle">{index + 1}</Text>
                </TableTd>
                {row.map((cell, cellIndex) => (
                  <TableTd key={cellIndex}>{cell}</TableTd>
                ))}
              </TableTr>
            ))}
          </TableTbody>
        </Table>
      </Box>
    </Box>
  );
}

type PreviewErrorsProps = {
  errors: readonly string[];
  /** The workspace identifies people by email, so an id-only query can switch identifiers. */
  canIdentifyByEmail: boolean;
  /** Jump to the Settings step to change the identifier; absent once the import is active. */
  onGoToSettings?: () => void;
};

/** The blocking validations a preview raised, one banner each. */
export function PreviewErrors({ errors, canIdentifyByEmail, onGoToSettings }: PreviewErrorsProps) {
  return (
    <>
      {errors.map((error) => {
        const copy = previewErrorCopy(error, 'people');
        const offersSettings = copy.key === 'id_not_present' && canIdentifyByEmail;
        return (
          <Banner key={error} variant="critical" title={copy.title} mt="200" data-test-create-sql-import-alert="errors">
            <BannerDescription>
              {offersSettings ? (
                <>
                  You chose to identify people by id, but didn't specify one in your query. Include an id column or
                  switch to identify by email in the sync{' '}
                  {onGoToSettings == null ? (
                    'settings'
                  ) : (
                    <PlainButton onClick={onGoToSettings} data-test-sql-import-settings-button>
                      settings
                    </PlainButton>
                  )}
                  .
                </>
              ) : (
                copy.message
              )}
            </BannerDescription>
          </Banner>
        );
      })}
    </>
  );
}

type PreviewWarningsProps = {
  warnings: readonly string[];
};

/** The non-blocking validations a preview raised, one banner each. */
export function PreviewWarnings({ warnings }: PreviewWarningsProps) {
  return (
    <>
      {warnings.map((warning) => {
        const copy = previewWarningCopy(warning);
        return (
          <Banner
            key={warning}
            variant="caution"
            title={copy.title}
            mt="200"
            data-test-create-sql-import-alert="warnings"
          >
            <BannerDescription>
              {copy.message}
              {copy.learnMoreUrl == null ? null : (
                <>
                  {' '}
                  <Link href={copy.learnMoreUrl} variant="secondary" isExternal>
                    Learn more
                  </Link>
                </>
              )}
            </BannerDescription>
          </Banner>
        );
      })}
    </>
  );
}
